import { DATLaneHistoryEntry } from 'lib/api/getDATLaneHistory';
import { QuickQuoteResponseStop } from 'lib/api/getQuickQuote';

import { DATLaneHistoryChartEntry } from '../LaneHistory/DATLaneHistory';
import { QuoteCountries, QuoteFormStop } from '../types';

export function toTitleCase(str: string): string {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatDATLaneHistoryRates(
  rates: DATLaneHistoryEntry[]
): DATLaneHistoryChartEntry[] {
  return rates.map((rate) => {
    // Ensure we have valid numbers, defaulting to 0 if not
    const averageRate =
      rate.perTrip &&
      typeof rate.perTrip.rateUsd === 'number' &&
      !isNaN(rate.perTrip.rateUsd)
        ? rate.perTrip.rateUsd
        : 0;
    const maxRate =
      rate.perTrip &&
      typeof rate.perTrip.highUsd === 'number' &&
      !isNaN(rate.perTrip.highUsd)
        ? rate.perTrip.highUsd
        : 0;
    const lowestRate =
      rate.perTrip &&
      typeof rate.perTrip.lowUsd === 'number' &&
      !isNaN(rate.perTrip.lowUsd)
        ? rate.perTrip.lowUsd
        : 0;

    const date = new Date(rate.year, rate.month - 1);
    return {
      month: `${date.toLocaleString('default', { month: 'long' })} ${rate.year}`,
      averageRate,
      maxRate,
      lowestRate,
    };
  });
}

export function convertFormStopsToResponseStops(
  stops: QuoteFormStop[]
): QuickQuoteResponseStop[] {
  return stops.map((stop) => ({
    order: stop.order,
    city: stop.city,
    state: stop.state,
    zip: stop.zip,
    country: stop.country as QuoteCountries,
  }));
}

export const formatDollarsNoCents = (value?: number): string => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return 'N/A';
  }
  return `$${value.toFixed(0)}`;
};

/** Buy-rate band bounds as returned by the API (keeps cents, e.g. $1,219.46). */
export function formatBuyRateBandDollars(value: number): string {
  if (!Number.isFinite(value)) {
    return 'N/A';
  }
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** Ordinal suffix for display (e.g. 20 → "20th"). */
export function formatOrdinal(n: number): string {
  if (!Number.isFinite(n)) {
    return String(n);
  }
  const abs = Math.abs(Math.trunc(n));
  const j = abs % 10;
  const k = abs % 100;
  const suffix =
    j === 1 && k !== 11
      ? 'st'
      : j === 2 && k !== 12
        ? 'nd'
        : j === 3 && k !== 13
          ? 'rd'
          : 'th';
  return `${n}${suffix}`;
}

export const formatUpToFiveDecimals = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '';
  }
  const fixed = value.toFixed(5);
  return fixed.replace(/\.?0+$/, '');
};
